<template>
  <div
    class="f-column mh-0 w-100"
    style="gap: 1rem; max-height: 100%"
  >
    <div
      class="f-column mh-0"
      style="gap: 0.5rem"
    >
      <GwFilter
        v-for="(conf, idx) in [filterConfigType, filterConfigStake]"
        :key="idx"
        :asRow="true"
        :hasSaveBtn="false"
        :hasClearBtn="false"
        :hasUrlUpdate="false"
        :filterConfig="conf"
        style="max-width: 50rem"
        @update:filter="
          filterConf => handleFilterUpdate(filterConf, conf.categories[0].filters[0].field_name)
        "
      />
    </div>

    <TableBody
      ref="tablebody"
      :items="l_items"
      :isInfinite="false"
      size="small"
      class="mh-0"
      style="
        min-height: 0;
        --overflow-y-head: scroll;
        --overflow-y-body-scroller: scroll;
        --min-height-table: clamp(1px, 42rem, 70vh);
        --max-height-table: 70vh;
      "
      :data-tst="`${tstPrefix}_table`"
    >
      <template #head_before>
        <TableHead
          :sortConf="tbl_sortConf"
          :thead="thead"
          class="large-scroll-track"
          @click:th="tbl_handleSort"
        >
        </TableHead>
      </template>
      <template #default="{ item, index }">
        <RakesTableRow
          :tstPrefix="`row_${index}`"
          :thead="thead"
          :item="item"
          @click="_e => $emit('click:item', { item })"
        >
        </RakesTableRow>
      </template>
    </TableBody>
  </div>
</template>

<script>
import TableHead from '@/components/DataTables/v2/TableHead'
import TableBody from '@/components/DataTables/v2/TableBody'
import RakesTableRow from '@/components/DataTables/v2/tables/RakesTable/RakesTableRow'
import table_mix from '@/components/DataTables/table_mix'
import GwFilter from '@/components/DataTables/Filters/new/GwFilter'

const getFilterConfig = ({ items = [], key }) => ({
  categories: [
    {
      category_name: 'general',
      key,
      filters: [key].map(field_name => {
        const options = [...new Set(items.reduce((acc, item) => [...acc, item[field_name]], []))]
        if (key === 'stake') {
          options.sort((a, b) => Number(a.replace('NL', '')) - Number(b.replace('NL', '')))
        } else {
          options.sort()
        }

        return {
          field_name,
          filter_type: 'MULTIPLE_CHOICE',
          filter_ui_type: 'SELECT_EXPOSED',
          available_choices: options,
        }
      }),
    },
  ],
})

const getFilterBase = () => ({
  loaded: false,
  data: null,
  params: null,
  active: false,
})

export default {
  name: 'RakesTable',
  components: {
    TableHead,
    TableBody,
    RakesTableRow,
    GwFilter,
  },
  mixins: [table_mix],
  props: {
    items: { type: Array, default: () => [] },
    tstPrefix: String,
  },
  emits: ['click:item'],
  data() {
    return {
      tbl_sortConf: {
        sortKey: 'type',
        sortOrder: 'asc',
      },

      // all of this nested filtering could be refactored to neat little config
      // so we'll have support for nested filters TODO-nested filters
      filters: {
        type: getFilterBase(),
        stake: getFilterBase(),
      },
    }
  },
  methods: {
    handleFilterUpdate(filterConf, key) {
      this.filters[key] = filterConf
    },
  },
  computed: {
    thead() {
      const cols = {
        type: {
          name: this.$t('table_head.type'),
          sortKey: 'type',
          width: 12,
        },
        stake: {
          name: this.$t('table_head.stake'),
          sortKey: 'stake',
          width: 12,
        },
        rakePercent: {
          name: this.$t('table_head.rake_percentage'),
          sortKey: 'rakePercent',
          width: 5,
        },
        rakeCap: {
          name: this.$t('table_head.rake_cap'),
          sortKey: 'rakeCap',
          width: 8,
        },
        preflopRakeType: {
          name: this.$t('table_head.preflop_rake_type'),
          sortKey: 'preflopRakeType',
          width: 12,
        },
      }
      const res = {}
      Object.keys(cols).forEach(key => {
        const col = cols[key]
        const width = col.width || col.minWidth
        res[key] = {
          key,
          ...col,
          cssStyle: !width
            ? ''
            : `
            --min-width-th: ${width}rem;
            ${col.minWidth ? '' : `--max-width-th: ${width}rem`};
            `,
          tdStyle: !width
            ? ''
            : `
            --min-width-td: ${width}rem;
            ${col.minWidth ? '' : `--max-width-td: ${width}rem`};
            `,
        }
      })
      return res
    },
    filterConfigType() {
      return getFilterConfig({
        items: this.items,
        key: 'type',
      })
    },
    itemsFilteredByType() {
      const tpl = this.filters.type.params?.type?.length
      return this.items.filter(item =>
        tpl ? this.filters.type.params.type.includes(item.type) : true,
      )
    },
    filterConfigStake() {
      return getFilterConfig({
        items: this.itemsFilteredByType,
        key: 'stake',
      })
    },
    itemsFilteredByStake() {
      const stl = this.filters.stake.params?.stake?.length
      return this.itemsFilteredByType.filter(item =>
        stl ? this.filters.stake.params.stake.includes(item.stake) : true,
      )
    },
    l_items() {
      const res = this.itemsFilteredByStake
      const { sortKey, sortOrder } = this.tbl_sortConf

      const getVal =
        sortKey === 'stake'
          ? item => Number(item[sortKey].replace('NL', ''))
          : item => item[sortKey]
      if (sortOrder === 'desc') {
        res.sort((a, b) => (getVal(a) > getVal(b) ? -1 : 1))
      } else {
        res.sort((a, b) => (getVal(a) < getVal(b) ? -1 : 1))
      }
      return res.map(i => i) // to re-trigger reactivity
    },
  },
}
</script>
